import { getEntityById, getLabelFromId } from '@core/utils/collection';
import {
  idToForm,
  optionalNumber,
  optionalTrimmed,
  toFormValue,
} from '@core/utils/form';
import type {
  ChasubleItem,
  CreateChasubleItemData,
} from '../../../../api/order';
import { VestmentCategory } from '../../../enum/vestmentCategory';
import {
  type ChasublePanelField,
  chasubleValuesFromApi,
  getBodyTypeLabel,
  getChasubleFeatures,
  getChasublePanelFields,
  getGothicTypeLabel,
  getPatchingTypeLabel,
  hasChasubleSleeveDesign,
  showsChasubleCollar,
  showsChasublePanel,
} from '../../chasuble';
import { getCollarLabel, usesVestmentCollars } from '../../collar';
import { chasubleSizeFields } from '../../size';
import {
  defaultPanelLength,
  defaultPanelWidth,
  displayMeasurement,
} from '../defaults';
import {
  EMPTY_LINE_ITEM_BASE,
  baseFieldsFromApi,
  baseLineItemPayload,
  fabricSegment,
  formatMaterialLabel,
  formatOrderItemSizeParts,
  getChasubleMaterial,
  getMaterialSummary,
  measurementPart,
  segment,
  segments,
  withSegmentColor,
  withSegmentHex,
} from '../helpers';
import type { ChasubleItemFormValues, OrderItemCategoryConfig } from '../types';

export const chasubleConfig: OrderItemCategoryConfig = {
  label: 'Chasuble',
  emptyFormValues: (): ChasubleItemFormValues => ({
    ...EMPTY_LINE_ITEM_BASE,
    ...chasubleValuesFromApi({}),
    length: '',
    sleeve: '',
    neck: '',
    bodyType: '',
    centerFinish: '',
    panelWidth: '',
    panelLength: '',
  }),
  apiToFormValues: (item): ChasubleItemFormValues => {
    const chasuble = item as ChasubleItem;
    return {
      ...baseFieldsFromApi(chasuble),
      ...chasubleValuesFromApi(chasuble),
      length: toFormValue(chasuble.length),
      sleeve: toFormValue(chasuble.sleeve),
      neck: toFormValue(chasuble.neck),
      bodyType: chasuble.bodyType ?? '',
      centerFinish: toFormValue(chasuble.centerFinish),
      panelWidth: toFormValue(chasuble.panelWidth),
      panelLength: toFormValue(chasuble.panelLength),
      collar: idToForm(chasuble.collar ?? chasuble.legacyCollar),
    };
  },
  formToPayload: (item, vestmentType): CreateChasubleItemData => {
    const chasuble = item as ChasubleItemFormValues;
    const collar = optionalNumber(chasuble.collar);
    return {
      ...baseLineItemPayload(chasuble),
      length: optionalNumber(chasuble.length),
      sleeve: optionalNumber(chasuble.sleeve),
      neck: optionalNumber(chasuble.neck),
      bodyFabric: optionalNumber(chasuble.bodyFabric),
      bodyColor: optionalNumber(chasuble.bodyColor),
      bodyType: optionalTrimmed(chasuble.bodyType),
      panelFabric: optionalNumber(chasuble.panelFabric),
      panelColor: optionalNumber(chasuble.panelColor),
      centerFinish: optionalNumber(chasuble.centerFinish),
      panelWidth: optionalNumber(chasuble.panelWidth),
      panelLength: optionalNumber(chasuble.panelLength),
      patchingType: optionalTrimmed(chasuble.patchingType),
      thread: optionalNumber(chasuble.thread),
      trimming: optionalNumber(chasuble.trimming),
      gothicType: optionalTrimmed(chasuble.gothicType),
      panelDimension: optionalTrimmed(chasuble.panelDimension),
      galoon: optionalNumber(chasuble.galoon),
      liningFabric: optionalNumber(chasuble.liningFabric),
      liningColor: optionalNumber(chasuble.liningColor),
      stoleSize: optionalTrimmed(chasuble.stoleSize),
      ...(usesVestmentCollars(vestmentType)
        ? { collar }
        : { legacyCollar: collar }),
      collarFabric: optionalNumber(chasuble.collarFabric),
      collarColor: optionalNumber(chasuble.collarColor),
    };
  },
  formatSummarySegments: (item, ctx) => {
    const chasuble = item as ChasubleItemFormValues;
    const vestment = getEntityById(ctx.vestments, chasuble.vestment);
    const design = chasuble.design;
    const selectedSize = getEntityById(ctx.sizes, chasuble.size);
    const forCustomer = ctx.customer;
    const material = getChasubleMaterial(vestment?.type, chasuble, ctx);
    const panelFields = showsChasublePanel(vestment?.type)
      ? getChasublePanelFields(vestment?.type, chasuble)
      : [];
    const panelPart = (field: ChasublePanelField): string | null => {
      switch (field) {
        case 'fabric':
          return getLabelFromId(ctx.fabrics, chasuble.panelFabric);
        case 'color':
          return getLabelFromId(ctx.colors, chasuble.panelColor);
        case 'width':
          return measurementPart(
            'W',
            displayMeasurement(
              chasuble.panelWidth,
              defaultPanelWidth(chasuble.centerFinish, design?.centerFinish)
            )
          );
        case 'length':
          return measurementPart(
            'L',
            displayMeasurement(
              chasuble.panelLength,
              defaultPanelLength({
                customer: forCustomer,
                size: selectedSize,
                vestmentType: vestment?.type,
                vestmentCategory: VestmentCategory.Chasuble,
                lineItemLength: chasuble.length,
              })
            )
          );
        case 'gothicType':
          return getGothicTypeLabel(chasuble.gothicType);
        case 'galoon':
          return getMaterialSummary(ctx.galoons, chasuble.galoon);
        case 'dimension':
          return chasuble.panelDimension;
        case 'stoleSize':
          return chasuble.stoleSize ? `Stole ${chasuble.stoleSize}` : null;
        default:
          return null;
      }
    };
    return segments(
      segment(
        'Size',
        ...formatOrderItemSizeParts(chasubleSizeFields(vestment?.type), {
          sizeName: getLabelFromId(ctx.sizes, chasuble.size),
          size: selectedSize,
          customer: forCustomer,
          vestmentType: vestment?.type,
          length: chasuble.length,
          sleeve: chasuble.sleeve,
          neck: chasuble.neck,
        })
      ),
      withSegmentColor(
        segment(
          'Body',
          getChasubleFeatures(vestment?.type)?.bodyType
            ? getBodyTypeLabel(chasuble.bodyType)
            : null,
          hasChasubleSleeveDesign(vestment?.type, chasuble.bodyType)
            ? chasuble.designCode
            : null,
          getLabelFromId(ctx.fabrics, chasuble.bodyFabric),
          getLabelFromId(ctx.colors, chasuble.bodyColor)
        ),
        ctx.colors,
        chasuble.bodyColor
      ),
      panelFields.length
        ? withSegmentColor(
            segment('Panel', ...panelFields.map(panelPart)),
            ctx.colors,
            chasuble.panelColor
          )
        : null,
      panelFields.includes('patching')
        ? withSegmentHex(
            segment(
              'Patching',
              getPatchingTypeLabel(chasuble.patchingType),
              material ? formatMaterialLabel(material) : null
            ),
            material?.hexCode
          )
        : null,
      panelFields.includes('trimming') && material
        ? withSegmentHex(
            segment('Trimming', formatMaterialLabel(material)),
            material.hexCode
          )
        : null,
      panelFields.includes('liningFabric')
        ? fabricSegment(
            'Lining',
            ctx.fabrics,
            ctx.colors,
            chasuble.liningFabric,
            chasuble.liningColor
          )
        : null,
      showsChasubleCollar(vestment?.type)
        ? withSegmentColor(
            segment(
              'Collar',
              getCollarLabel(vestment, ctx.collars, chasuble.collar),
              getLabelFromId(ctx.fabrics, chasuble.collarFabric),
              getLabelFromId(ctx.colors, chasuble.collarColor)
            ),
            ctx.colors,
            chasuble.collarColor
          )
        : null
    );
  },
};
